/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2023 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2023 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */
import React from 'react';
import type { ApiModule, ApiUrls } from '@opengeoweb/api';
import { ErrorBoundary } from '@opengeoweb/shared';
import type { ExpandedWarningSections } from '../../store/warningsFilters/types';
import type { PickedFilterState } from '../FilterList/filter-hooks';
import { MaritimeWarningsFormConnect } from '../MaritimeWarningsForm/MaritimeWarningsFormConnect';
import type { MaritimeWarningsDomain } from '../MaritimeWarningsForm/types';
import { PublicWarningListConnect } from '../PublicWarningList';
import { WarningsTimelineConnect } from '../WarningsTimeline';

const warningListViewPreset = {
  title: 'Warning List',
  id: 'warninglist',
  componentType: 'WarningList',
  initialProps: {},
};

const maritimeWarningsViewPreset = {
  title: 'Maritime Warnings',
  id: 'maritimewarnings',
  componentType: 'MaritimeWarnings',
  initialProps: {},
};

const warningTimelineViewPreset = {
  title: 'Warning Timeline',
  id: 'warningtimeline',
  componentType: 'WarningsTimeline',
  initialProps: {},
};

export type WarningListComponentType =
  | 'WarningList'
  | 'MaritimeWarnings'
  | 'WarningsTimeline';

interface WarningsInitialProps {
  filterState?: PickedFilterState;
  expandedSections?: ExpandedWarningSections;
  domain?: MaritimeWarningsDomain;
}
export interface ComponentsLookUpPayload {
  componentType: WarningListComponentType;
  id: string;
  title?: string;
  initialProps: WarningsInitialProps;
}

// base type for connected modules
export interface ApiModuleProps extends ApiModule {
  config: ApiUrls;
}

export const componentsLookUp = (
  payload: ComponentsLookUpPayload,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ComponentsLookUpWrapper: any = null,
): React.ReactElement => {
  const { id, componentType, initialProps } = payload;
  switch (componentType) {
    case warningListViewPreset.componentType:
      return (
        <ErrorBoundary>
          <PublicWarningListConnect
            data-testid="publicWarningList"
            panelId={id}
            presetExpandedSections={initialProps.expandedSections}
            presetFilterState={initialProps.filterState}
          />
        </ErrorBoundary>
      );
    case maritimeWarningsViewPreset.componentType:
      if (ComponentsLookUpWrapper) {
        return (
          <ErrorBoundary>
            <ComponentsLookUpWrapper
              component={MaritimeWarningsFormConnect}
              domain={initialProps.domain || 'northSeaDistricts'}
              payload={payload}
            />
          </ErrorBoundary>
        );
      }
      return (
        <ErrorBoundary>
          <MaritimeWarningsFormConnect
            domain={initialProps.domain || 'northSeaDistricts'}
          />
        </ErrorBoundary>
      );
    case warningTimelineViewPreset.componentType:
      return (
        <ErrorBoundary>
          <WarningsTimelineConnect panelId={id} />
        </ErrorBoundary>
      );
    default:
      return null!;
  }
};
